import React, { useEffect, useRef, useState } from "react";
import "./Navbar.css";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from "./LanguageContext";

const menuSections = [
  {
    key: "kultura",
    label: "KULTURA",
    items: [
      { to: "kultura/muzea", name: "Muzea", label: "MUZEA" },
      { to: "kultura/teatry", name: "Teatry", label: "TEATRY" },
      { to: "kultura/kina", name: "Kina", label: "KINA" },
      { to: "kultura/zabytki", name: "Zabytki", label: "ZABYTKI" },
      { to: "kultura/kulturaInne", name: "KulturaInne", label: "INNE" },
    ],
  },
  {
    key: "gastronomia",
    label: "GASTRONOMIA",
    items: [
      { to: "gastronomia/restauracje", name: "Restauracje", label: "RESTAURACJE" },
      { to: "gastronomia/fastFoody", name: "FastFoody", label: "FAST FOODY" },
      { to: "gastronomia/kawiarnie", name: "Kawiarnie", label: "KAWIARNIE" },
      { to: "gastronomia/desery", name: "Desery", label: "DESERY" },
      { to: "gastronomia/gastronomiaInne", name: "GastronomiaInne", label: "INNE" },
    ],
  },
  {
    key: "nauka",
    label: "NAUKA",
    items: [
      { to: "nauka/biblioteki", name: "Biblioteki", label: "BIBLIOTEKI" },
      { to: "nauka/ksiegarnie", name: "Księgarnie", label: "KSIĘGARNIE" },
      { to: "nauka/kursy", name: "Kursy", label: "KURSY" },
      { to: "nauka/targi", name: "Targi", label: "TARGI I KIERMASZE" },
      { to: "nauka/naukaInne", name: "NaukaInne", label: "INNE" },
    ],
  },
];

const sectionClassMap = {
  kultura: "show1",
  gastronomia: "show2",
  nauka: "show3",
};

function Navbar(props) {
  const { t } = useLanguage();
  const [navPosition, setNavPosition] = useState("-100%");
  const [activeSection, setActiveSection] = useState(null);
  const sectionTimer = useRef(null);

  function clearSectionTimer() {
    if (sectionTimer.current) {
      window.clearTimeout(sectionTimer.current);
      sectionTimer.current = null;
    }
  }

  function supportsHover() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  function handleClick(event) {
    scrollWindowTop();
    const category = event.currentTarget.name;

    if (!category) return;

    clearSectionTimer();
    setNavPosition("-100%");
    setActiveSection(null);
    props.handleClick(category);
  }

  function scrollWindowTop() {
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  function handleClickStart() {
    clearSectionTimer();
    scrollWindowTop();
    setNavPosition("-100%");
    setActiveSection(null);
  }

  function toggleMenuButton() {
    setNavPosition((current) => (current === "0" ? "-100%" : "0"));
  }

  function handleArrowClick(key) {
    clearSectionTimer();
    setActiveSection(key);
  }

  function handleSectionMouseEnter(key) {
    if (!supportsHover()) return;

    clearSectionTimer();
    sectionTimer.current = window.setTimeout(() => {
      setActiveSection(key);
    }, 160);
  }

  function handleSectionMouseLeave() {
    if (!supportsHover()) return;

    clearSectionTimer();
    sectionTimer.current = window.setTimeout(() => {
      setActiveSection(null);
    }, 80);
  }

  useEffect(() => () => clearSectionTimer(), []);

  useEffect(() => {
    const shouldLockPage = navPosition === "0" && !supportsHover();

    if (shouldLockPage) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [navPosition]);

  function getUserName(username) {
    if (!username) return "KONTO";
    if (username.length > 10) return `${username.substring(0, 7)}...`;
    return username;
  }

  const userName = props.userData?.username;
  const isMenuOpen = navPosition === "0";
  const navLinksClassName = activeSection ? `nav-links ${sectionClassMap[activeSection]}` : "nav-links";
  const isEnglish = props.language === "en";

  return (
    <nav>
      <div className="navbar">
        <button type="button" className="nav-icon-button" onClick={toggleMenuButton} aria-label="Otwórz menu">
          <FontAwesomeIcon className="bx bx-menu" icon={faBars} />
        </button>

        <div className="logo">
          <NavLink to={"/"} onClick={handleClickStart} name="Start">znane<span>M</span>iasto.pl</NavLink>
        </div>

        <button
          type="button"
          className={`mobile-backdrop ${isMenuOpen ? "is-visible" : ""}`}
          onClick={toggleMenuButton}
          aria-label="Zamknij menu"
        />

        <div className={navLinksClassName} style={{ left: navPosition }}>
          <div className="sidebar-logo">
            <NavLink to={"/"} onClick={handleClickStart} name="Start">
              <span className="logo-name">znane<span>M</span>iasto.pl</span>
            </NavLink>
            <button type="button" className="nav-icon-button" onClick={toggleMenuButton} aria-label="Zamknij menu">
              <FontAwesomeIcon className="bx bx-x" icon={faXmark} />
            </button>
          </div>

          <ul className="links">
            <li><NavLink to={"eventy"} onClick={handleClick} name="Events">{t("events")}</NavLink></li>

            {menuSections.map(({ key, label, items }) => (
              <li
                key={key}
                className={activeSection === key ? "active" : ""}
                onMouseEnter={() => handleSectionMouseEnter(key)}
                onMouseLeave={handleSectionMouseLeave}
              >
                <div className="category-controls">
                  <button
                    type="button"
                    className="nav-label-button"
                    onClick={() => handleArrowClick(key)}
                    aria-expanded={activeSection === key}
                  >
                    {t({ kultura: "culture", gastronomia: "food", nauka: "learning" }[key])}
                  </button>
                  <button
                    type="button"
                    className="nav-arrow-button"
                    onClick={() => handleArrowClick(key)}
                    aria-label={`Rozwiń ${label}`}
                  >
                    <FontAwesomeIcon className={`${key}-arrow arrow`} icon={faChevronDown} />
                  </button>
                </div>
                <ul className={`${key}-sub-menu sub-menu`}>
                  {items.map(({ to, name, label: itemLabel }) => (
                    <li key={name}>
                      <NavLink to={to} onClick={handleClick} name={name} end>{t({ Muzea: "museums", Teatry: "theatres", Kina: "cinemas", Zabytki: "monuments", KulturaInne: "other", Restauracje: "restaurants", FastFoody: "fastFood", Kawiarnie: "cafes", Desery: "desserts", GastronomiaInne: "other", Biblioteki: "libraries", Księgarnie: "bookstores", Kursy: "courses", Targi: "fairs", NaukaInne: "other" }[name])}</NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ))}

            <li><NavLink to={"szukaj"} onClick={handleClick} name="Search">{t("search")}</NavLink></li>

            {userName ? (
              <li>
                <NavLink to={"uzytkownik"} onClick={handleClick} name="UserAccount">{getUserName(userName)}</NavLink>
              </li>
            ) : (
              <li>
                <NavLink to={"logowanie"} onClick={handleClick} name="UserLogin">{t("account")}</NavLink>
              </li>
            )}
          </ul>
          <div className="nav-preferences">
            <button
              className="theme-toggle-nav"
              type="button"
              onClick={props.onThemeToggle}
              aria-label={props.theme === "dark" ? "Włącz tryb jasny" : "Włącz tryb ciemny"}
              aria-pressed={props.theme === "dark"}
              title={props.theme === "dark" ? "Włącz tryb jasny" : "Włącz tryb ciemny"}
            />
            <button
              className="language-toggle-nav"
              type="button"
              onClick={props.onLanguageToggle}
              aria-label={isEnglish ? "Przełącz na język polski" : "Switch to English"}
              title={isEnglish ? "Przełącz na język polski" : "Switch to English"}
            >
              {isEnglish ? "EN" : "PL"}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;